/** @file SlotCard: one image slot (direction, state, thumbnail or grey silhouette, size, pivot). */
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import type { CSSProperties, MouseEvent, ReactElement } from 'react';
import type { ArtSlot, SlotState } from './artTypes';
import { useThumbnail } from './useThumbnail';

/** The card's slot, its shown state, whether it is selected, the preview bundle its thumbnail is cut from. */
export type SlotCardProps = {
  slot: ArtSlot; state: SlotState; selected: boolean; bundle: LoadedBundle | null; maskUrl?: string | null;
  onSelect: (key: string) => void; onMenu: (key: string, x: number, y: number) => void;
  /** Double click: paint the slot in its Tile tab. */
  onOpen?: (key: string) => void;
};

const STATE_COLOR: Record<SlotState, string> = { filled: '#3fa66b', empty: '#8a8f98', warning: '#d9a227' };
const PIXELATED: CSSProperties = { imageRendering: 'pixelated', width: '100%', height: '100%', objectFit: 'contain' };

/** The empty look: a grey silhouette box of the slot's aspect with the pivot (joint pixel) marked. */
function Silhouette({ slot, maskUrl }: { slot: ArtSlot; maskUrl: string | null }): ReactElement {
  if (maskUrl !== null) return <img src={maskUrl} alt={`mask ${slot.key}`} style={PIXELATED} />;
  const [w, h] = slot.size;
  const left = `${(slot.pivot[0] / Math.max(w, 1)) * 100}%`;
  const top = `${(slot.pivot[1] / Math.max(h, 1)) * 100}%`;
  return (
    <div data-testid="silhouette" style={{ position: 'relative', height: '100%', aspectRatio: `${w} / ${h}`, margin: '0 auto', background: '#3a3d44', borderRadius: 2 }}>
      <span style={{ position: 'absolute', left, top, width: 4, height: 4, marginLeft: -2, marginTop: -2, background: '#e05555', borderRadius: 2 }} />
    </div>
  );
}

/** Left click selects the card, double click opens its Tile tab, right click selects it and opens the slot menu. */
export function SlotCard({ slot, state, selected, bundle, maskUrl = null, onSelect, onMenu, onOpen }: SlotCardProps): ReactElement {
  const thumbnail = useThumbnail(slot.status === 'filled' ? bundle : null, slot);
  const contextMenu = (e: MouseEvent): void => {
    e.preventDefault();
    onSelect(slot.key);
    onMenu(slot.key, e.clientX, e.clientY);
  };
  return (
    <div
      role="button"
      tabIndex={-1}
      aria-pressed={selected}
      data-slot={slot.key}
      data-state={state}
      title={`${slot.key} · ${slot.band}`}
      onClick={() => { onSelect(slot.key); }}
      onDoubleClick={() => { onOpen?.(slot.key); }}
      onContextMenu={contextMenu}
      style={{
        display: 'flex', flexDirection: 'column', gap: 4, padding: 6, borderRadius: 6, cursor: 'pointer', userSelect: 'none',
        background: selected ? '#2b3a55' : '#23262d', border: `1px solid ${selected ? '#5b8def' : '#33363d'}`, fontSize: 11,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 12 }}>{slot.direction}</strong>
        <span data-testid="state" style={{ color: STATE_COLOR[state] }}>{state}</span>
      </div>
      <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1c21', borderRadius: 4 }}>
        {thumbnail === null
          ? <Silhouette slot={slot} maskUrl={slot.status === 'empty' ? maskUrl : null} />
          : <img src={thumbnail} alt={`tile ${slot.key}`} style={PIXELATED} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#a9adb6' }}>
        <span>{slot.size[0]}x{slot.size[1]}</span>
        <span>pivot {slot.pivot[0]},{slot.pivot[1]}</span>
      </div>
    </div>
  );
}
